import { useMemo } from 'react';

import { SJDatabase } from '../core';
import { ISjCollection } from '../core/types';
import { DtoId } from '../models/base.dto';
import { CollectionName } from '../types';

import { useSJDatabase } from './useSjDatabase';

export const useSjActions = <
  TColKey extends keyof ColKeyToColNameMapType,
  TColName extends CollectionName = ColKeyToColNameMapType[TColKey],
>(
  name: TColKey,
): ISjCollection<TColName> & {
  isDbReady: boolean;
  generateId: () => DtoId;
} => {
  // TODO think about assert
  const collectionName = ColKeyToColNameMap[name] as unknown as TColName;
  const sjdb = useSJDatabase();

  return useMemo(() => {
    const getAll: ISjCollection<TColName>['getAll'] = () => {
      const collection = sjdb.collections?.[collectionName];
      if (!collection) throw new Error('useSjActions.getAll');

      return collection.getAll();
    };

    const create: ISjCollection<TColName>['create'] = (value, publicKeys, permissions) => {
      const collection = sjdb.collections?.[collectionName];
      if (!collection) throw new Error('useSJActions.create');

      // if (value.id) throw new Error('[useSjActions] dont use id');
      // value.id = SJDatabase.generateId(collectionName);

      return collection.create(value, publicKeys, permissions);
    };

    const update: ISjCollection<TColName>['update'] = (value) => {
      const collection = sjdb.collections?.[collectionName];
      if (!collection) throw new Error('useSJActions.update');

      return collection.update(value);
    };

    const updateMany: ISjCollection<TColName>['updateMany'] = (values) => {
      const collection = sjdb.collections?.[collectionName];
      if (!collection) throw new Error('useSJActions.updateMany');

      return collection.updateMany(values);
    };

    const findById: ISjCollection<TColName>['findById'] = (id) => {
      const collection = sjdb.collections?.[collectionName];
      if (!collection) throw new Error('useSJActions.findById');

      return collection.findById(id);
    };

    const findOne: ISjCollection<TColName>['findOne'] = (condition) => {
      const collection = sjdb.collections?.[collectionName];
      if (!collection) throw new Error('useSJActions.findOne');

      return collection.findOne(condition);
    };

    const findMany: ISjCollection<TColName>['findMany'] = (condition, sort, skip, limit) => {
      const collection = sjdb.collections?.[collectionName];
      if (!collection) throw new Error('useSJActions.findMany');

      return collection.findMany(condition, sort, skip, limit);
    };

    const remove: ISjCollection<TColName>['remove'] = (value) => {
      const collection = sjdb.collections?.[collectionName];
      if (!collection) throw new Error('useSJActions.remove');

      return collection.remove(value);
    };

    const removeById: ISjCollection<TColName>['removeById'] = (id) => {
      const collection = sjdb.collections?.[collectionName];
      if (!collection) throw new Error();

      return collection.removeById(id);
    };

    return {
      create,
      update,
      updateMany,
      getAll,
      findById,
      findOne,
      findMany,
      remove,
      removeById,

      // Ext
      generateId: () => SJDatabase.generateId(collectionName),
      // TODO rework. Value means collections looks like ready
      isDbReady: sjdb.isDbReady,
    };
  }, [name, sjdb]);
};

const ColKeyToColNameMap = {
  assetInformationDocument: CollectionName.ASSET_INFORMATION_DOCUMENT,
  assets: CollectionName.ASSETS,
  assistantMnemonic: CollectionName.ASSISTANT_MNEMONIC,
  beneficiaries: CollectionName.BENEFICIARIES,
  beneficiaryPersonalDataDocuments: CollectionName.BENEFICIARY_PERSONAL_DATA_DOCUMENT,
  contacts: CollectionName.CONTACTS,
  delegatingRequests: CollectionName.DELEGATING_REQUESTS,
  encryptedFileKey: CollectionName.ENCRYPTED_FILE_KEY,
  fileMetaInfos: CollectionName.FILE_META_INFO,
  inbox: CollectionName.INBOX,
  interwills: CollectionName.INTERWILLS,
  myHub: CollectionName.MY_HUB,
  notification: CollectionName.NOTIFICATION,
  personalIdentifiersDocuments: CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS,
  privateDocuments: CollectionName.PRIVATE_DOCUMENTS,
  scenarioMetaInfos: CollectionName.SCENARIO_META_INFO,
  sowe: CollectionName.SOWE_ATTACHMENTS,
  soweAttachments: CollectionName.SOWE,
} as const;

type ColKeyToColNameMapType = typeof ColKeyToColNameMap;
